/**
 * ForemanLogSessionCard · Phase 2B (Rev 3 §4.5)
 * ─────────────────────────────────────────────────────────────────────
 * Foreman "Log Today's Session" card. Sits at the top of the Job
 * Execution panel and feeds `/api/schedule/events/{id}/sessions/{n}/log`.
 *
 * CRITICAL DESIGN CONSTRAINT (Rev 3 Q3 · Pass 4 A5 · Guardrails):
 *   This card is BLIND TO ESTIMATES. It never renders
 *   `estimated_hours` or `estimated_quantity` from the planned session.
 *   Anchoring bias would corrupt the calibrator observations.
 *
 * Fields captured per log:
 *   - actual_hours    (mandatory unless status=skipped)
 *   - crew_size       (mandatory · Pass 3 A1)
 *   - work_rate_key   (mandatory · Pass 4 C4 · defaults to session's key)
 *   - quantity_completed  (mandatory when actual_hours > 0)
 *   - weather         (enum · optional)
 *   - cure_check_result  (enum · only when session role = cure_gap)
 *   - status          (partial | complete | skipped)
 *   - skip_reason     (≥ 10 chars when status = skipped)
 *   - notes           (free-text)
 *
 * Tactical Industrial aesthetic — matches design_guidelines.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CalendarDays, ClipboardCheck, AlertTriangle, X } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const WEATHER_OPTIONS = [
  { v: '', label: '— not recorded —' },
  { v: 'sunny_dry', label: 'Sunny / Dry' },
  { v: 'wet', label: 'Wet' },
  { v: 'windy', label: 'Windy' },
  { v: 'cold', label: 'Cold' },
  { v: 'cloudy', label: 'Cloudy' },
  { v: 'hot', label: 'Hot' },
  { v: 'other', label: 'Other' },
];

const CURE_CHECK_OPTIONS = [
  { v: '', label: '— not checked —' },
  { v: 'cured_ok', label: 'Cured OK' },
  { v: 'cured_slow', label: 'Cured slow (extend gap)' },
  { v: 'cured_early_override', label: 'Cured early — override (needs ≥ 20-char note)' },
  { v: 'not_cured_reschedule', label: 'Not cured — reschedule' },
];

const STATUS_OPTIONS = [
  { v: 'complete', label: 'Complete' },
  { v: 'partial', label: 'Partial (more work tomorrow)' },
  { v: 'skipped', label: 'Skipped' },
];

const SESSION_ROLE_LABELS = {
  prep: 'Prep', strip: 'Strip', install: 'Install', bed: 'Bed', point: 'Point',
  seal: 'Seal', coat: 'Coat', rescrew: 'Rescrew', rust: 'Rust', wash: 'Wash',
  cure_gap: 'Cure gap', cleanup: 'Cleanup', qc_inspect: 'QC inspect',
  followup: 'Follow-up', inspect: 'Inspect',
};

const SESSION_STATUS_STYLES = {
  pending: 'border-l-slate-400 bg-slate-50 text-slate-700',
  in_progress: 'border-l-sky-500 bg-sky-50 text-sky-800',
  complete: 'border-l-emerald-600 bg-emerald-50 text-emerald-800',
  partial: 'border-l-amber-500 bg-amber-50 text-amber-800',
  skipped: 'border-l-slate-500 bg-slate-100 text-slate-600 line-through',
  cure_active: 'border-l-violet-500 bg-violet-50 text-violet-800',
  cure_elapsed: 'border-l-violet-700 bg-violet-100 text-violet-900',
};

export default function ForemanLogSessionCard({ appraisalId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [openSessionNo, setOpenSessionNo] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/schedule/events/by-appraisal/${appraisalId}`);
      setEvents(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0 && !selectedEventId) {
        setSelectedEventId(data[0]._id || data[0].id);
      }
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [appraisalId, selectedEventId]);

  useEffect(() => { load(); }, [load]);

  const selectedEvent = useMemo(
    () => events.find(e => (e._id || e.id) === selectedEventId) || null,
    [events, selectedEventId],
  );

  const openLogFor = (session) => {
    setOpenSessionNo(session.session_no);
    setForm({
      ...INITIAL_FORM,
      work_rate_key: session.work_rate_key || '',
      role_actual: session.role || 'install',
      _session_role: session.role,
    });
    setSubmitErr('');
    setOkMsg('');
  };

  const closeLog = () => {
    setOpenSessionNo(null);
    setForm(INITIAL_FORM);
    setSubmitErr('');
  };

  const submit = async () => {
    if (!selectedEvent || openSessionNo === null) return;
    setSubmitErr('');

    // Client-side echo of backend guards (better UX; backend still authoritative)
    if (form.status !== 'skipped') {
      if (!(Number(form.actual_hours) > 0)) {
        setSubmitErr('actual_hours must be > 0 (unless skipped).');
        return;
      }
      if (!(Number(form.quantity_completed) > 0)) {
        setSubmitErr('quantity_completed must be > 0 when hours are logged.');
        return;
      }
    }
    if (!(Number(form.crew_size) >= 1)) {
      setSubmitErr('crew_size must be at least 1.');
      return;
    }
    if (!form.work_rate_key || !form.work_rate_key.trim()) {
      setSubmitErr('work_rate_key is required.');
      return;
    }
    if (form.status === 'skipped' && (form.skip_reason || '').trim().length < 10) {
      setSubmitErr('skip_reason must be at least 10 characters.');
      return;
    }
    if (form.cure_check_result === 'cured_early_override'
        && (form.cure_check_notes || '').trim().length < 20) {
      setSubmitErr('cured_early_override requires notes ≥ 20 characters.');
      return;
    }

    const eid = selectedEvent._id || selectedEvent.id;
    const payload = {
      actual_hours: Number(form.actual_hours) || 0,
      crew_size: Number(form.crew_size),
      work_rate_key: form.work_rate_key.trim(),
      quantity_completed: Number(form.quantity_completed) || 0,
      status: form.status,
      role_actual: form.role_actual || null,
      weather: form.weather || null,
      cure_check_result: form.cure_check_result || null,
      cure_check_notes: form.cure_check_notes || '',
      supplied_workers: [],
      skip_reason: form.status === 'skipped' ? form.skip_reason : '',
      notes: form.notes || '',
    };

    setSubmitting(true);
    try {
      await api.post(`/schedule/events/${eid}/sessions/${openSessionNo}/log`, payload);
      setOkMsg(`Session ${openSessionNo} logged.`);
      closeLog();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSubmitErr(typeof detail === 'string' ? detail : 'Log failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!appraisalId) return null;
  if (loading) return (
    <div data-testid="foreman-log-loading"
         className="border-l-4 border-l-slate-300 bg-white p-4 text-sm text-slate-500">
      Loading scheduled sessions…
    </div>
  );
  if (events.length === 0) return (
    <div data-testid="foreman-log-empty"
         className="border-l-4 border-l-slate-400 bg-slate-50 p-4 text-sm text-slate-600">
      <div className="flex items-center gap-2 font-semibold text-slate-700">
        <CalendarDays className="w-4 h-4" /> No scheduled sessions
      </div>
      <p className="mt-1 text-slate-500">
        This appraisal has no schedule event yet. Book it on the
        <a href="/schedule" className="ml-1 underline hover:text-slate-800">
          Schedule page
        </a>{' '}
        to enable the Foreman log.
      </p>
    </div>
  );

  return (
    <div data-testid="foreman-log-card"
         className="border-l-4 border-l-sky-600 bg-white">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm tracking-wide">
          <ClipboardCheck className="w-4 h-4" /> LOG TODAY'S SESSION
        </div>
        {events.length > 1 && (
          <select
            data-testid="foreman-log-event-select"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="border border-slate-300 px-2 py-1 text-xs font-mono"
          >
            {events.map(ev => (
              <option key={ev._id || ev.id} value={ev._id || ev.id}>
                {ev.start_date} · {ev.event_type} · {ev.status}
              </option>
            ))}
          </select>
        )}
      </div>

      {okMsg && (
        <div className="px-4 py-2 text-xs text-emerald-700 bg-emerald-50 border-b border-emerald-200"
             data-testid="foreman-log-ok">
          {okMsg}
        </div>
      )}

      {selectedEvent && (
        <div className="p-4">
          {(selectedEvent.sessions || []).length === 0 ? (
            <div className="text-xs text-slate-500">
              This event has no session breakdown. Add sessions on the
              Schedule page to log per-session actuals.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="foreman-log-sessions">
              {(selectedEvent.sessions || []).map((s) => (
                <li key={s.session_no}
                    className={`border-l-4 px-3 py-2 flex items-center justify-between ${
                      SESSION_STATUS_STYLES[s.status] || SESSION_STATUS_STYLES.pending
                    }`}
                    data-testid={`foreman-log-session-${s.session_no}`}>
                  <div className="text-xs">
                    <div className="font-mono font-semibold">
                      #{s.session_no} · {SESSION_ROLE_LABELS[s.role] || s.role}
                      {s.role === 'cure_gap' && s.cure_reason && (
                        <span className="ml-2 text-violet-700">
                          ({s.cure_reason})
                        </span>
                      )}
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      {s.start_date}
                      {s.end_date && s.end_date !== s.start_date && ` → ${s.end_date}`}
                      {' · '}
                      <span className="uppercase tracking-wide">
                        {s.status || 'pending'}
                      </span>
                    </div>
                  </div>
                  {!['complete', 'skipped'].includes(s.status) && (
                    <button
                      onClick={() => openLogFor(s)}
                      data-testid={`foreman-log-btn-${s.session_no}`}
                      className="bg-slate-900 text-white px-3 py-1 text-xs font-semibold uppercase tracking-wide hover:bg-slate-700"
                    >
                      Log
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {openSessionNo !== null && selectedEvent && (
        <LogForm
          sessionNo={openSessionNo}
          form={form}
          setForm={setForm}
          submitErr={submitErr}
          submitting={submitting}
          onCancel={closeLog}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

const INITIAL_FORM = {
  actual_hours: '',
  crew_size: '',
  work_rate_key: '',
  quantity_completed: '',
  status: 'complete',
  role_actual: '',
  weather: '',
  cure_check_result: '',
  cure_check_notes: '',
  skip_reason: '',
  notes: '',
  _session_role: '',
};

function LogForm({ sessionNo, form, setForm, submitErr, submitting, onCancel, onSubmit }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isCureSession = form._session_role === 'cure_gap';
  const isSkipped = form.status === 'skipped';

  return (
    <div className="border-t-2 border-slate-900 bg-slate-50 p-4"
         data-testid="foreman-log-form">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-slate-800 text-sm tracking-wide">
          LOG SESSION #{sessionNo}
        </div>
        <button onClick={onCancel} className="text-slate-500 hover:text-slate-800"
                data-testid="foreman-log-cancel">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Field label="Status">
          <select value={form.status} onChange={set('status')}
                  data-testid="foreman-log-status"
                  className="w-full border border-slate-300 px-2 py-1.5 font-mono">
            {STATUS_OPTIONS.map(o => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Actual hours" required={!isSkipped}>
          <input type="number" step="0.25" min="0" value={form.actual_hours}
                 onChange={set('actual_hours')} disabled={isSkipped}
                 data-testid="foreman-log-hours"
                 className="w-full border border-slate-300 px-2 py-1.5 font-mono disabled:bg-slate-200" />
        </Field>

        <Field label="Crew size" required>
          <input type="number" min="1" step="1" value={form.crew_size}
                 onChange={set('crew_size')}
                 data-testid="foreman-log-crew"
                 className="w-full border border-slate-300 px-2 py-1.5 font-mono" />
        </Field>

        <Field label="Quantity completed" required={!isSkipped}>
          <input type="number" step="0.1" min="0" value={form.quantity_completed}
                 onChange={set('quantity_completed')} disabled={isSkipped}
                 data-testid="foreman-log-qty"
                 className="w-full border border-slate-300 px-2 py-1.5 font-mono disabled:bg-slate-200" />
        </Field>

        <Field label="Work rate key" required className="md:col-span-2">
          <input type="text" value={form.work_rate_key}
                 onChange={set('work_rate_key')}
                 data-testid="foreman-log-rate-key"
                 placeholder="e.g. concrete_bedding"
                 className="w-full border border-slate-300 px-2 py-1.5 font-mono" />
        </Field>

        <Field label="Weather">
          <select value={form.weather} onChange={set('weather')}
                  data-testid="foreman-log-weather"
                  className="w-full border border-slate-300 px-2 py-1.5 font-mono">
            {WEATHER_OPTIONS.map(o => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </Field>

        {isCureSession && (
          <Field label="Cure check result">
            <select value={form.cure_check_result}
                    onChange={set('cure_check_result')}
                    data-testid="foreman-log-cure-check"
                    className="w-full border border-slate-300 px-2 py-1.5 font-mono">
              {CURE_CHECK_OPTIONS.map(o => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
          </Field>
        )}

        {isCureSession && form.cure_check_result === 'cured_early_override' && (
          <Field label="Cure override justification (≥ 20 chars)" required
                 className="col-span-2 md:col-span-4">
            <textarea value={form.cure_check_notes}
                      onChange={set('cure_check_notes')} rows={2}
                      data-testid="foreman-log-cure-notes"
                      className="w-full border border-slate-300 px-2 py-1.5" />
          </Field>
        )}

        {isSkipped && (
          <Field label="Skip reason (≥ 10 chars)" required
                 className="col-span-2 md:col-span-4">
            <textarea value={form.skip_reason} onChange={set('skip_reason')}
                      rows={2} data-testid="foreman-log-skip-reason"
                      className="w-full border border-slate-300 px-2 py-1.5" />
          </Field>
        )}

        <Field label="Notes" className="col-span-2 md:col-span-4">
          <textarea value={form.notes} onChange={set('notes')} rows={2}
                    data-testid="foreman-log-notes"
                    className="w-full border border-slate-300 px-2 py-1.5" />
        </Field>
      </div>

      {submitErr && (
        <div className="mt-3 border-l-4 border-l-rose-600 bg-rose-50 text-rose-800 text-xs px-3 py-2 flex items-start gap-2"
             data-testid="foreman-log-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{submitErr}</span>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button onClick={onSubmit} disabled={submitting}
                data-testid="foreman-log-submit"
                className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wide hover:bg-slate-700 disabled:opacity-50">
          {submitting ? 'Logging…' : `Submit log for #${sessionNo}`}
        </button>
        <button onClick={onCancel}
                data-testid="foreman-log-cancel-btn"
                className="border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-700 hover:bg-slate-100">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, className = '', children }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-slate-600 uppercase tracking-wide text-[10px] font-semibold">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {children}
    </label>
  );
}
